import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "../../../utils/api";
import { LoginHelper } from "../../../utils/LoginHelper";
import { useAdminGate } from "../../../utils/useAdminGate";
import DoctorMultiSelect from "../../../components/DoctorMultiSelect";
import Breadcrumb from "../../common/breadcrumb/Breadcrumb";
import DetailAccentCard from "../../common/detail/DetailAccentCard";
import EditorFieldSection from "../../common/editor/EditorFieldSection";
import {
	editorFileLabelClassName,
	editorInputClassName,
	editorLabelClassName,
} from "../../common/editor/editorStyles";
import Footer from "../../common/footer/Footer";
import Header from "../../common/header/Header";
import CatalogEmptyState from "../../common/catalog/CatalogEmptyState";
import Paginator from "../../common/paginator/Paginator";
import DashboardSidebar from "../DashboardSidebar";
import AdminVideoClipListItem from "./AdminVideoClipListItem";
import { normalizeAdminClipListResponse } from "./adminClipListResponse";
import FieldExpertise from "./FieldExpertise";
import { fileInputSummary } from "./fileInputLabel";
import { expertiseIdsFromClip } from "./clipExpertiseUtils";

const emptyForm = {
	title: "",
	description: "",
	access_type: "subscription",
	credit_cost: "1",
	video_url: "",
	video_file: null,
	image_file: null,
	is_active: true,
	instructor_ids: [],
	expertise_ids: [],
};

export default function AdminVideoClips() {
	const location = useLocation();
	const [isDesktop, setIsDesktop] = useState(window.innerWidth > 768);
	const [clips, setClips] = useState([]);
	const [listQuery, setListQuery] = useState("");
	const [appliedQuery, setAppliedQuery] = useState("");
	const [page, setPage] = useState(1);
	const [maxPageNum, setMaxPageNum] = useState(1);
	const [totalCount, setTotalCount] = useState(0);
	const [listLoading, setListLoading] = useState(false);
	const [form, setForm] = useState(emptyForm);
	const [editingId, setEditingId] = useState(null);
	const [error, setError] = useState("");
	const [notice, setNotice] = useState("");
	const [loading, setLoading] = useState(false);
	const [expertiseSelectionSeed, setExpertiseSelectionSeed] = useState(0);
	const [formVisible, setFormVisible] = useState(false);
	const formSectionRef = useRef(null);
	const focusClipIdRef = useRef(location.state?.editClipId ?? null);
	const openedFocusClipRef = useRef(false);
	const legacyAllClipsRef = useRef(null);
	const listSectionRef = useRef(null);
	const { ready, isAdmin } = useAdminGate();

	const fetchClips = useCallback(
		(currentPage, currentQuery, focusClipId, { forceNetwork = false } = {}) => {
			const normalizeOpts = {
				page: currentPage,
				query: currentQuery,
				focusClipId,
			};

			const applyNormalized = (raw) => {
				if (Array.isArray(raw)) {
					legacyAllClipsRef.current = raw;
				} else {
					legacyAllClipsRef.current = null;
				}
				const normalized = normalizeAdminClipListResponse(raw, normalizeOpts);
				setClips(normalized.clips);
				setMaxPageNum(normalized.max_page);
				setPage(normalized.page);
				setTotalCount(normalized.total_count);
				return normalized;
			};

			if (!forceNetwork && legacyAllClipsRef.current) {
				setListLoading(true);
				const normalized = applyNormalized(legacyAllClipsRef.current);
				setListLoading(false);
				return Promise.resolve(normalized);
			}

			setListLoading(true);
			return api
				.videoClipAdminList({
					token: LoginHelper.getToken(),
					page: currentPage,
					query: currentQuery,
					...(focusClipId ? { focus_clip_id: focusClipId } : {}),
				})
				.then(applyNormalized)
				.catch((err) => {
					setError(
						err?.data?.message || "دریافت کلیپ‌ها ناموفق بود"
					);
					return null;
				})
				.finally(() => setListLoading(false));
		},
		[]
	);

	function applyListSearch(event) {
		event?.preventDefault?.();
		if (listLoading) return;
		const next = listQuery.trim();
		if (next !== appliedQuery) {
			setAppliedQuery(next);
			setPage(1);
			return;
		}
		fetchClips(page, appliedQuery, undefined, { forceNetwork: true });
	}

	function scrollToForm() {
		requestAnimationFrame(() => {
			formSectionRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		});
	}

	useEffect(() => {
		const resize = () => setIsDesktop(window.innerWidth > 768);
		window.addEventListener("resize", resize);
		return () => window.removeEventListener("resize", resize);
	}, []);

	useEffect(() => {
		if (!isAdmin) return;
		const focusId =
			focusClipIdRef.current && !openedFocusClipRef.current
				? focusClipIdRef.current
				: undefined;
		const forceNetwork =
			Boolean(focusId) || legacyAllClipsRef.current == null;
		fetchClips(page, appliedQuery, focusId, { forceNetwork });
	}, [isAdmin, page, appliedQuery, fetchClips]);

	useEffect(() => {
		if (!focusClipIdRef.current || openedFocusClipRef.current) return;
		const target = clips.find(
			(item) => String(item.id) === String(focusClipIdRef.current)
		);
		if (!target) return;
		openedFocusClipRef.current = true;
		editClip(target);
	}, [clips]);

	function startCreate() {
		setEditingId(null);
		setForm(emptyForm);
		setExpertiseSelectionSeed((value) => value + 1);
		setNotice("");
		setError("");
		setFormVisible(true);
		scrollToForm();
	}

	function editClip(clip) {
		setFormVisible(true);
		setEditingId(clip.id);
		setForm({
			title: clip.title || "",
			description: clip.description || "",
			access_type: clip.access_type || "subscription",
			credit_cost: String(clip.credit_cost || 1),
			video_url: clip.video_url || "",
			video_file: null,
			image_file: null,
			clear_video_file: false,
			has_video_file: clip.has_video_file,
			is_active: clip.is_active,
			instructor_ids: clip.instructors || [],
			instructors_detail: clip.instructors_detail || [],
			expertise_ids: expertiseIdsFromClip(clip),
		});
		setNotice("");
		setError("");
		setExpertiseSelectionSeed((value) => value + 1);
		scrollToForm();
	}

	function closeForm() {
		setFormVisible(false);
		setEditingId(null);
		setForm(emptyForm);
		setExpertiseSelectionSeed((value) => value + 1);
		setNotice("");
		setError("");
	}

	function saveClip(event) {
		event.preventDefault();
		setLoading(true);
		setError("");
		setNotice("");
		api
			.videoClipSave({
				token: LoginHelper.getToken(),
				...(editingId ? { clip_id: editingId } : {}),
				title: form.title,
				description: form.description,
				access_type: form.access_type,
				credit_cost:
					form.access_type === "credit" ? Number(form.credit_cost) : 0,
				video_url: form.video_url || "",
				...(form.video_file ? { video_file: form.video_file } : {}),
				...(form.image_file ? { image_file: form.image_file } : {}),
				...(form.clear_video_file ? { clear_video_file: true } : {}),
				is_active: form.is_active,
				instructor_ids: JSON.stringify(form.instructor_ids),
				expertise_ids: JSON.stringify(form.expertise_ids || []),
			})
			.then((savedClip) => {
				const savedId = savedClip?.id ?? editingId;
				const wasEdit = Boolean(editingId);
				setNotice(wasEdit ? "کلیپ ویرایش شد" : "کلیپ ساخته شد");
				return fetchClips(page, appliedQuery, savedId, {
					forceNetwork: true,
				}).then(
					(normalized) => {
						const list = normalized?.clips ?? [];
						const refreshed =
							list.find(
								(item) => String(item.id) === String(savedId)
							) || savedClip;
						if (refreshed) {
							editClip(refreshed);
						} else if (!wasEdit) {
							closeForm();
						}
					}
				);
			})
			.catch((err) =>
				setError(err?.data?.message || "ذخیره کلیپ ناموفق بود")
			)
			.finally(() => setLoading(false));
	}

	function removeClip(clip) {
		if (!window.confirm(`کلیپ «${clip.title}» حذف شود؟`)) return;
		api
			.videoClipRemove({ token: LoginHelper.getToken(), clip_id: clip.id })
			.then(() => {
				if (String(editingId) === String(clip.id)) {
					closeForm();
				}
				fetchClips(page, appliedQuery, undefined, {
					forceNetwork: true,
				}).then((normalized) => {
					if (
						normalized &&
						normalized.clips.length === 0 &&
						page > 1
					) {
						setPage(page - 1);
					}
				});
			})
			.catch((err) =>
				setError(err?.data?.message || "حذف کلیپ ناموفق بود")
			);
	}

	if (!ready) return null;
	if (!isAdmin) return <Navigate to="/login/" replace />;

	return (
		<>
			<Helmet>
				<title>مدیریت ویدیو کلیپ | همراهتیم</title>
			</Helmet>
			<div className="flex min-h-screen flex-col justify-between bg-[#F0F6FC]">
				<div>
					<Header isDesktop={isDesktop} />
					<div className="bg-white">
						<div className="mx-auto max-w-[1200px] px-4">
							<Breadcrumb
								linkDataList={[
									{ label: "خانه", href: "/" },
									{ label: "داشبورد مدیریت", href: "/dashboard/" },
									{
										label: "ویدیو کلیپ",
										href: "/dashboard/video-clips/",
									},
								]}
							/>
						</div>
					</div>
					<main
						className={[
							"mx-auto flex w-full max-w-[1200px] gap-5 py-8",
							isDesktop ? "flex-row px-4" : "flex-col p-[10px]",
						].join(" ")}
					>
						<DashboardSidebar isDesktop={isDesktop} />
						<section className="flex flex-1 flex-col gap-6">
							<div ref={listSectionRef}>
							<DetailAccentCard bodyClassName="p-5 md:p-6">
								<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
									<div>
										<h1 className="font-Vazir-800 text-[22px] text-darkBlue md:text-[24px]">
											مدیریت ویدیو کلیپ
										</h1>
										<p className="mt-1 font-Vazir-400 text-[13px] text-darkBlue/55">
											{totalCount} کلیپ
											{maxPageNum > 1
												? ` · صفحه ${page} از ${maxPageNum}`
												: ""}
											{appliedQuery ? ` · جستجو: «${appliedQuery}»` : ""}
										</p>
									</div>
									<div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
										<form
											onSubmit={applyListSearch}
											className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center"
										>
											<input
												type="search"
												value={listQuery}
												onChange={(e) => setListQuery(e.target.value)}
												placeholder="جستجو در عنوان یا توضیح…"
												className={`${editorInputClassName} w-full sm:max-w-[220px]`}
											/>
											<button
												type="submit"
												disabled={listLoading}
												className="shrink-0 rounded-[10px] border border-darkBlue/20 px-4 py-2.5 font-Vazir-600 text-[13px] text-darkBlue disabled:opacity-60"
											>
												جستجو
											</button>
										</form>
										<button
											type="button"
											onClick={startCreate}
											className="shrink-0 rounded-[12px] bg-cta px-5 py-2.5 font-Vazir-700 text-[14px] text-white shadow-sm transition hover:bg-cta/90"
										>
											+ کلیپ جدید
										</button>
									</div>
								</div>

								{!formVisible && notice && (
									<p className="mt-4 rounded-[10px] bg-green-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-green-800">
										{notice}
									</p>
								)}
								{!formVisible && error && (
									<p className="mt-4 rounded-[10px] bg-red-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-[#cc0000]">
										{error}
									</p>
								)}

								<div
									className={`mt-4 grid gap-3 transition-opacity ${listLoading ? "opacity-60" : ""}`}
								>
									{clips.map((clip) => (
										<AdminVideoClipListItem
											key={clip.id}
											clip={clip}
											isEditing={
												formVisible &&
												String(editingId) === String(clip.id)
											}
											onEdit={editClip}
											onRemove={removeClip}
										/>
									))}
									{!listLoading &&
										totalCount === 0 &&
										!appliedQuery && (
										<CatalogEmptyState
											message="هنوز کلیپی ساخته نشده است."
											hint="روی «کلیپ جدید» بزنید و فرم را پر کنید."
											actionLabel="ساخت اولین کلیپ"
											onAction={startCreate}
										/>
									)}
									{!listLoading &&
										clips.length === 0 &&
										appliedQuery && (
										<p className="py-8 text-center font-Vazir-400 text-midGray">
											کلیپی با این عنوان پیدا نشد.
										</p>
									)}
								</div>

								{maxPageNum > 1 && (
									<div className="mt-6 flex justify-center pt-2">
										<Paginator
											currentPage={page}
											maxPageNum={maxPageNum}
											isLoading={listLoading}
											onChange={setPage}
											scrollToTop={false}
											scrollAnchorRef={listSectionRef}
										/>
									</div>
								)}
							</DetailAccentCard>
							</div>

							{formVisible && (
								<div ref={formSectionRef}>
								<DetailAccentCard bodyClassName="p-5 md:p-6">
									<div className="flex flex-wrap items-start justify-between gap-3">
										<div>
											<h2 className="font-Vazir-800 text-[20px] text-darkBlue md:text-[22px]">
												{editingId ? "ویرایش کلیپ" : "ساخت کلیپ جدید"}
											</h2>
											<p className="mt-2 max-w-2xl font-Vazir-400 text-[14px] leading-relaxed text-darkBlue/60">
												اطلاعات را مرحله‌به‌مرحله تکمیل کنید؛ برای
												کلیپ‌های قفل‌شده فایل یا لینک ویدیو لازم است.
											</p>
										</div>
										<button
											type="button"
											onClick={closeForm}
											className="rounded-[10px] border border-darkBlue/20 px-4 py-2 font-Vazir-600 text-[13px] text-darkBlue"
										>
											بستن فرم
										</button>
									</div>

								<form
									onSubmit={saveClip}
									className="mt-6 flex flex-col gap-5"
								>
									<div>
										<EditorFieldSection
											title="اطلاعات اصلی"
											description="عنوان و توضیح کوتاه در صفحه جزئیات کلیپ نمایش داده می‌شود."
										>
											<div>
												<label className={editorLabelClassName}>
													عنوان
												</label>
												<input
													required
													value={form.title}
													onChange={(e) =>
														setForm({ ...form, title: e.target.value })
													}
													placeholder="مثلاً: مدیریت استرس روزانه"
													className={editorInputClassName}
												/>
											</div>
											<div>
												<label className={editorLabelClassName}>
													توضیحات
												</label>
												<textarea
													value={form.description}
													onChange={(e) =>
														setForm({
															...form,
															description: e.target.value,
														})
													}
													placeholder="خلاصهٔ محتوای کلیپ برای کاربر"
													className={`${editorInputClassName} min-h-[96px] resize-y`}
													rows={3}
												/>
											</div>
										</EditorFieldSection>
									</div>

									<div>
										<EditorFieldSection
											title="دسترسی و ویدیو"
											description="رایگان برای همه، یا نیاز به اشتراک / اعتبار."
										>
											<div
												className={
													isDesktop
														? "grid grid-cols-2 gap-4"
														: "grid grid-cols-1 gap-4"
												}
											>
												<div>
													<label className={editorLabelClassName}>
														نوع دسترسی
													</label>
													<select
														value={form.access_type}
														onChange={(e) =>
															setForm({
																...form,
																access_type: e.target.value,
															})
														}
														className={editorInputClassName}
													>
														<option value="free">
															رایگان برای همه
														</option>
														<option value="subscription">
															نیازمند اشتراک فعال
														</option>
														<option value="credit">
															کسر اعتبار اشتراک
														</option>
													</select>
												</div>
												{form.access_type === "credit" && (
													<div>
														<label className={editorLabelClassName}>
															هزینه (اعتبار)
														</label>
														<input
															required
															min="1"
															type="number"
															value={form.credit_cost}
															onChange={(e) =>
																setForm({
																	...form,
																	credit_cost: e.target.value,
																})
															}
															className={editorInputClassName}
														/>
													</div>
												)}
											</div>
											<div>
												<label className={editorLabelClassName}>
													لینک ویدیو (اختیاری)
												</label>
												<input
													value={form.video_url}
													onChange={(e) =>
														setForm({
															...form,
															video_url: e.target.value,
														})
													}
													placeholder="اگر فایل آپلود می‌کنید می‌توانید خالی بگذارید"
													className={editorInputClassName}
													dir="ltr"
												/>
											</div>
											<div
												className={
													isDesktop
														? "grid grid-cols-2 gap-4"
														: "grid grid-cols-1 gap-4"
												}
											>
												<label className={editorFileLabelClassName}>
													<span className="font-Vazir-600 text-darkBlue/80">
														فایل ویدیو
													</span>
													<span className="text-[12px] text-darkBlue/50">
														{fileInputSummary(form.video_file)}
														{editingId &&
															form.has_video_file &&
															!form.video_file &&
															" · فایل فعلی روی سرور"}
													</span>
													<input
														type="file"
														accept="video/*"
														onChange={(e) =>
															setForm({
																...form,
																video_file:
																	e.target.files?.[0] || null,
															})
														}
														className="text-sm text-darkBlue/70"
													/>
												</label>
												<label className={editorFileLabelClassName}>
													<span className="font-Vazir-600 text-darkBlue/80">
														تصویر کاور
													</span>
													<span className="text-[12px] text-darkBlue/50">
														{fileInputSummary(form.image_file)}
													</span>
													<input
														type="file"
														accept="image/*"
														onChange={(e) =>
															setForm({
																...form,
																image_file:
																	e.target.files?.[0] || null,
															})
														}
														className="text-sm text-darkBlue/70"
													/>
												</label>
											</div>
											{editingId && form.has_video_file && (
												<label className="flex items-center gap-2 font-Vazir-400 text-[14px] text-darkBlue/70">
													<input
														type="checkbox"
														checked={form.clear_video_file}
														onChange={(e) =>
															setForm({
																...form,
																clear_video_file: e.target.checked,
															})
														}
													/>
													حذف فایل ویدیویی فعلی از سرور
												</label>
											)}
										</EditorFieldSection>
									</div>

									<div>
										<EditorFieldSection
											title="استاد و تخصص"
											description="نام استاد در کارت و صفحه جزئیات؛ تخصص برای فیلتر و پیشنهاد کلیپ."
										>
											<DoctorMultiSelect
												value={form.instructor_ids}
												initialDoctors={form.instructors_detail || []}
												onChange={(instructor_ids) =>
													setForm({ ...form, instructor_ids })
												}
											/>
											<div key={editingId ?? "new-clip"}>
												<FieldExpertise
													clipId={editingId}
													initialExpertiseIds={form.expertise_ids}
													selectionSeed={expertiseSelectionSeed}
													isDesktop={isDesktop}
													onChange={(expertise_ids) =>
														setForm((prev) => ({
															...prev,
															expertise_ids,
														}))
													}
												/>
											</div>
										</EditorFieldSection>
									</div>

									<div>
										<EditorFieldSection title="انتشار">
											<label className="flex items-center gap-2 font-Vazir-400 text-[14px] text-darkBlue">
												<input
													type="checkbox"
													checked={form.is_active}
													onChange={(e) =>
														setForm({
															...form,
															is_active: e.target.checked,
														})
													}
												/>
												نمایش برای کاربران (فعال)
											</label>
										</EditorFieldSection>
									</div>

									<div className="flex flex-wrap gap-2">
										<button
											disabled={loading}
											type="submit"
											className="rounded-[12px] bg-cta px-6 py-3 font-Vazir-700 text-white shadow-sm transition hover:bg-cta/90 disabled:opacity-60"
										>
											{loading
												? "در حال ذخیره..."
												: editingId
													? "ذخیره تغییرات"
													: "ساخت کلیپ"}
										</button>
										<button
											type="button"
											onClick={closeForm}
											className="rounded-[12px] border border-darkBlue/20 px-6 py-3 font-Vazir-600 text-darkBlue"
										>
											انصراف
										</button>
									</div>
								</form>

								{notice && (
									<p className="mt-4 rounded-[10px] bg-green-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-green-800">
										{notice}
									</p>
								)}
								{error && (
									<p className="mt-4 rounded-[10px] bg-red-50 px-4 py-3 text-center font-Vazir-500 text-[14px] text-[#cc0000]">
										{error}
									</p>
								)}
								</DetailAccentCard>
								</div>
							)}
						</section>
					</main>
				</div>
				<Footer isDesktop={isDesktop} />
			</div>
		</>
	);
}
